import React, { useCallback, forwardRef, memo } from 'react';
import ContentSection from '@/pages/Products/VpsOciDetailPage/commons/ContentSection';
import useLocale from '@/hooks/useLocale';
import * as Styles from './Management.styles';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import {
  IconArrow, Button,
} from 'gatorcomponents';
import { formatTimestampDate } from '@/utils/Formatters/formatTimestampDate';
import { useParams } from 'react-router-dom';
import {
  getIconBySlug,
  checkExistSlug,
  hasSlugAction,
  executeSlugAction,
} from '@/pages/Products/VpsOciDetailPage/utils';
import { billingAnalytics } from '@/analytics';
import { useIsMobile } from '@/hooks/useIsMobile';

import {
  ENABLE_VPS_OCI_MANAGEMENT_SO_APPS,
} from '@/config/GrowthBook/constants';
import { useFeatureIsOn } from '@growthbook/growthbook-react';

const Management = forwardRef((props, ref) => {
  const {
    onGoToTemplatesPage,
  } = props;

  const dispatch = useDispatch();
  const isMobile = useIsMobile();
  const {
    management,
    addonsList,
    serverIp,
    domain,
    productName,
  } = useSelector(state => ({
    management: state.vpsOci?.data?.management,
    addonsList: state.vpsOci?.data?.addons,
    serverIp: state.vpsOci?.data?.ssh?.serverIp,
    domain: state.vpsOci?.data?.vps?.domain,
    productName: state.vpsOci?.data?.productName,
  }), shallowEqual);
  const enableVpsOciManagementSoApps = useFeatureIsOn(ENABLE_VPS_OCI_MANAGEMENT_SO_APPS);

  const n8nState = useSelector(state => state.products?.n8n, shallowEqual);
  const { id: productId } = useParams();

  const { productDetails: productDetailsLocale } = useLocale();
  const { vpsOci: managementLocale } = productDetailsLocale;

  const getNameItem = useCallback((slug) => {
    if (!slug) return null;
    const item = managementLocale.management.items.find(itemLocale => itemLocale.slug === slug);
    return item ? item.name : '';
  }, [managementLocale.management.items]);

  const getInfoItem = useCallback((item) => {
    if (!item) return null;

    const { slug, info } = item;
    if (!slug && !info) return null;

    if (slug === 'so-apps') {
      const { version } = item;
      const name = (managementLocale.store.details.operationalSystems[info] && managementLocale.store.details.operationalSystems[info].name) || item.info;
      return name ? `${name}${version ? ` ${version}` : ''}` : '';
    }

    if (slug === 'backup' || slug === 'snapshot') {
      const { date } = item;
      if (!date) return info;
      return `${managementLocale.management.items.find(i => i.slug === slug).info} ${formatTimestampDate(date)}`;
    }

    return info;
  }, [managementLocale.store.details.operationalSystems, managementLocale.management.items]);

  const handleClick = useCallback((item) => {
    if (!item || !item.slug) return;

    if (item.slug === 'so-apps') {
      billingAnalytics.hostingAndServers.hostingManagementSoApplicationsClick(productName);
    }

    executeSlugAction(item.slug, {
      productId,
      serverIp,
      domain,
      onGoToTemplatesPage,
      n8nState,
      dispatch,
    });
  }, [productId, serverIp, domain, onGoToTemplatesPage, n8nState, dispatch, productName]);

  if ((!management || management.length === 0) && (!addonsList || addonsList.length === 0)) return null;

  let managementList = [
    ...(management || []),
    ...(addonsList || []),
  ];

  // Separar so-apps para renderização separada
  const soAppsItem = enableVpsOciManagementSoApps
    ? managementList.find(item => item.slug === 'so-apps')
    : null;

  // Filtrar so-apps e ssh-keys da lista principal
  managementList = managementList.filter(item => (item.slug !== 'so-apps') && (item.slug !== 'ssh-keys'));

  if (managementList.length === 0 && !soAppsItem) return null;

  return (
    <ContentSection
      ref={ref}
      direction="column"
      align="flex-start"
      justify="flex-start"
      title={managementLocale.management.title.text}
      tooltip={managementLocale.management.title.tooltip}
      testId="vps-management-section"
    >
      <Styles.ListItems>
        {managementList
          .filter(item => !checkExistSlug(item.slug, managementLocale))
          .map((item) => {
            const iconItem = getIconBySlug(item.slug);
            const itemName = getNameItem(item.slug);
            const itemInfo = getInfoItem(item);
            const itemHasHandler = hasSlugAction(item.slug);

            return (
              <Styles.Item
                key={item.slug}
                onClick={itemHasHandler ? () => handleClick(item) : undefined}
                $itemHasHandler={itemHasHandler}
                data-testid={`vps-management-item-${item.slug}`}
              >
                {iconItem && <Styles.ItemIcon src={iconItem} alt={item.slug} data-testid={`vpsoci-management-icon-${item.slug}`} />}
                <Styles.ItemContent>
                  {itemName && <Styles.ItemName data-testid="vps-management-name">{itemName}</Styles.ItemName>}
                  {itemInfo && <Styles.ItemInfo data-testid="vps-management-info">{itemInfo}</Styles.ItemInfo>}
                </Styles.ItemContent>
                {itemHasHandler && (
                  <Styles.ItemArrow>
                    <IconArrow color="primaryMedium" size="md" direction="right" />
                  </Styles.ItemArrow>
                )}
              </Styles.Item>
            );
          })}
      </Styles.ListItems>

      {soAppsItem && (() => {
        const { info, version } = soAppsItem;
        const soAppsIcon = getIconBySlug(info);
        const osName = managementLocale.store.details.operationalSystems[info]?.name || info;
        const soAppsDisplayName = `${osName}${version ? ` ${version}` : ''}`;

        return (
          <Styles.SoAppsSection>
            <Styles.SoAppsContainer data-testid="vps-management-item-so-apps">
              <Styles.SoAppsLeftContent>
                {soAppsIcon && (
                  <>
                    <Styles.SoAppsLabel>
                      {managementLocale.management.currentOS}
                    </Styles.SoAppsLabel>
                    <Styles.SoAppsInfo>
                      <Styles.SoAppsIcon
                        src={soAppsIcon}
                        alt={info}
                        data-testid="vpsoci-management-icon-so-apps"
                      />
                      <Styles.SoAppsText data-testid="vps-management-info">
                        {soAppsDisplayName}
                      </Styles.SoAppsText>
                    </Styles.SoAppsInfo>
                  </>
                )}
              </Styles.SoAppsLeftContent>
              <Styles.SoAppsButtonWrapper>
                <Button
                  variant="tertiary"
                  label={managementLocale.management.changeSoApps}
                  onClick={() => handleClick(soAppsItem)}
                  testId="vps-management-change-so-apps-button"
                  width={isMobile ? 'full' : 'fit-content'}
                  size="small"
                  version="v2"
                />
              </Styles.SoAppsButtonWrapper>
            </Styles.SoAppsContainer>
          </Styles.SoAppsSection>
        );
      })()}
    </ContentSection>
  );
});

Management.displayName = 'Management';

export default memo(Management);
